import { supabase } from '../lib/supabase'
import {
  type EmailSystem,
  type EmailTemplate,
  normalizeEmailSystem,
} from '../types/emailTemplates'

const BUCKET = 'email_templates'
const PENDING_PATH = 'pending'

function storagePath(system: EmailSystem, id: number): string {
  return `${system}/${id}.html`
}

function htmlContentType(file: File): string {
  return file.type || 'text/html'
}

function normalizeObs(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim()
  return trimmed ? trimmed : null
}

export const emailTemplatesService = {
  async list(filters?: { system?: EmailSystem | '' }): Promise<EmailTemplate[]> {
    let query = supabase
      .from('email_templates')
      .select('*')
      .is('deleted_at', null)
      .neq('html_storage_path', PENDING_PATH)
      .order('id', { ascending: false })

    if (filters?.system) {
      query = query.eq('system', normalizeEmailSystem(filters.system))
    }

    const { data, error } = await query
    if (error) throw error
    return (data || []) as EmailTemplate[]
  },

  async getById(id: number): Promise<EmailTemplate | null> {
    const { data, error } = await supabase
      .from('email_templates')
      .select('*')
      .eq('id', id)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    return (data as EmailTemplate) || null
  },

  async create(params: {
    subject: string
    sender: string
    system: EmailSystem
    file: File
    obs?: string | null
  }): Promise<EmailTemplate> {
    const { subject, sender, system: rawSystem, file, obs } = params
    const system = normalizeEmailSystem(rawSystem)
    const { data: authInfo } = await supabase.auth.getUser()
    const userId = authInfo?.user?.id ?? null

    const { data: row, error: insertError } = await supabase
      .from('email_templates')
      .insert({
        subject: subject.trim(),
        sender: sender.trim(),
        system,
        html_storage_path: PENDING_PATH,
        obs: normalizeObs(obs),
        created_by: userId,
      })
      .select('*')
      .single()

    if (insertError || !row) {
      throw insertError || new Error('Falha ao criar modelo de e-mail')
    }

    const id = (row as EmailTemplate).id
    const path = storagePath(system, id)

    try {
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: htmlContentType(file),
        })

      if (uploadError) throw uploadError

      const { data: updated, error: updateError } = await supabase
        .from('email_templates')
        .update({ html_storage_path: path })
        .eq('id', id)
        .is('deleted_at', null)
        .select('*')
        .single()

      if (updateError || !updated) throw updateError || new Error('Falha ao atualizar caminho do arquivo')

      return updated as EmailTemplate
    } catch (e) {
      await supabase.from('email_templates').delete().eq('id', id)
      throw e
    }
  },

  async update(
    id: number,
    params: {
      subject: string
      sender: string
      system: EmailSystem
      file?: File | null
      obs?: string | null
    }
  ): Promise<EmailTemplate> {
    const existing = await this.getById(id)
    if (!existing) throw new Error('Modelo não encontrado')

    const system = normalizeEmailSystem(params.system)
    const existingSystem = normalizeEmailSystem(existing.system)

    const newPath = storagePath(system, id)
    const oldPath = existing.html_storage_path

    if (params.file) {
      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .upload(newPath, params.file, {
          cacheControl: '3600',
          upsert: true,
          contentType: htmlContentType(params.file),
        })
      if (uploadError) throw uploadError

      if (oldPath && oldPath !== PENDING_PATH && oldPath !== newPath) {
        await supabase.storage.from(BUCKET).remove([oldPath])
      }
    } else if (system !== existingSystem && oldPath && oldPath !== PENDING_PATH) {
      const { data: blob, error: downloadError } = await supabase.storage.from(BUCKET).download(oldPath)
      if (downloadError) throw downloadError
      const { error: uploadError } = await supabase.storage.from(BUCKET).upload(newPath, blob, {
        cacheControl: '3600',
        upsert: true,
        contentType: 'text/html',
      })
      if (uploadError) throw uploadError
      await supabase.storage.from(BUCKET).remove([oldPath])
    }

    const { data, error } = await supabase
      .from('email_templates')
      .update({
        subject: params.subject.trim(),
        sender: params.sender.trim(),
        system,
        html_storage_path: params.file || system !== existingSystem ? newPath : oldPath,
        obs: normalizeObs(params.obs),
      })
      .eq('id', id)
      .is('deleted_at', null)
      .select('*')
      .single()

    if (error || !data) throw error || new Error('Falha ao atualizar modelo')
    return data as EmailTemplate
  },

  async remove(id: number): Promise<void> {
    const existing = await this.getById(id)
    if (!existing) return

    const { data: authInfo } = await supabase.auth.getUser()
    const userId = authInfo?.user?.id
    if (!userId) throw new Error('Usuário não autenticado')

    const { error } = await supabase
      .from('email_templates')
      .update({
        deleted_at: new Date().toISOString(),
        deleted_by: userId,
      })
      .eq('id', id)
      .is('deleted_at', null)

    if (error) throw error
  },

  async getHtmlPreviewUrl(path: string, expiresInSeconds = 300): Promise<string | null> {
    if (!path || path === PENDING_PATH) return null
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(path, expiresInSeconds)
    if (error) return null
    return data?.signedUrl ?? null
  },

  async downloadHtml(path: string): Promise<string> {
    if (!path || path === PENDING_PATH) {
      throw new Error('Arquivo HTML não disponível')
    }
    const { data, error } = await supabase.storage.from(BUCKET).download(path)
    if (error) throw error
    return await data.text()
  },
}
